import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { api, errMsg } from '../lib/api';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { GraduationCap, CheckCircle } from '@phosphor-icons/react';

export default function Apply() {
  const { code } = useParams();
  const [params] = useSearchParams();
  const embed = params.get('embed') === '1';
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ applicant_name: '', grade_applied: '5', guardian_name: '', guardian_email: '', guardian_phone: '', notes: '', website: '' });

  useEffect(() => {
    api.get(`/apply/${code}`).then((r) => setInfo(r.data)).catch((e) => setError(errMsg(e)));
  }, [code]);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/apply/${code}`, {
        ...form,
        utm_source: params.get('utm_source') || null,
        utm_medium: params.get('utm_medium') || null,
        utm_campaign: params.get('utm_campaign') || null,
      });
      setDone(true);
    } catch (e) { setError(errMsg(e)); } finally { setBusy(false); }
  };

  const card = (
    <div className="w-full max-w-md bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="apply-card">
      <div className="flex items-center gap-2 mb-1">
        <GraduationCap size={22} className="text-orange-600" weight="fill" />
        <h1 className="font-display text-xl font-extrabold tracking-tight">{info ? info.school_name : error ? 'Form not found' : 'Loading...'}</h1>
      </div>
      <p className="text-sm text-slate-500 mb-4">Admission inquiry form</p>
      {!info && error ? (
        <p className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md p-3" role="alert" data-testid="apply-error">{error}</p>
      ) : done ? (
        <div className="text-center py-8" data-testid="apply-success">
          <CheckCircle size={44} weight="fill" className="text-green-600 mx-auto mb-3" />
          <p className="font-display font-bold text-lg">Application received</p>
          <p className="text-sm text-slate-500 mt-1">The admissions team will contact you at {form.guardian_email}.</p>
        </div>
      ) : info && !info.enabled ? (
        <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3" data-testid="apply-closed">This form is not accepting applications right now. Please contact the school directly.</p>
      ) : (
        <div className="space-y-3">
          {error && <p className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-md p-2" role="alert" data-testid="apply-error">{error}</p>}
          <Input data-testid="apply-name" placeholder="Student name" value={form.applicant_name} onChange={(e) => setForm({ ...form, applicant_name: e.target.value })} />
          <select data-testid="apply-grade" value={form.grade_applied} onChange={(e) => setForm({ ...form, grade_applied: e.target.value })}
            className="w-full border border-slate-200 rounded-md px-2 py-2 text-sm bg-white">
            {(info?.grades || ['5']).map((g) => <option key={g} value={g}>{/^\d+$/.test(g) ? `Grade ${g}` : g}</option>)}
          </select>
          <Input data-testid="apply-guardian-name" placeholder="Parent / guardian name" value={form.guardian_name} onChange={(e) => setForm({ ...form, guardian_name: e.target.value })} />
          <Input data-testid="apply-guardian-email" type="email" placeholder="Guardian email" value={form.guardian_email} onChange={(e) => setForm({ ...form, guardian_email: e.target.value })} />
          <Input data-testid="apply-guardian-phone" placeholder="Guardian phone (optional)" value={form.guardian_phone} onChange={(e) => setForm({ ...form, guardian_phone: e.target.value })} />
          <textarea data-testid="apply-notes" placeholder="Anything the school should know (optional)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
            className="w-full border border-slate-200 rounded-md px-3 py-2 text-sm h-20" />
          <input type="text" name="website" value={form.website} onChange={(e) => setForm({ ...form, website: e.target.value })} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
          <Button data-testid="apply-submit" className="w-full bg-slate-900 hover:bg-slate-800"
            disabled={busy || !form.applicant_name || !form.guardian_name || !form.guardian_email} onClick={submit}>
            {busy ? 'Submitting...' : 'Submit application'}
          </Button>
          <p className="text-[11px] text-slate-400 text-center">Powered by ONRID · Know everyone. Trust the record.</p>
        </div>
      )}
    </div>
  );

  if (embed) return <div className="p-2 flex justify-center">{card}</div>;
  return <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">{card}</div>;
}
